import * as React from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';

import { TRUE_DARK, TRUE_MUTED } from '../auth/AuthLayout';
import { currency } from '../ui';
import { DEFAULT_BRAND, PublicBrand, fontStack, useFontsStylesheet } from './brand';

type OrderLine = { name: string; quantity: number; price: number; notes: string; options: { name: string }[]; confirmed?: boolean };
/** What the business's promos and the customer's reward took off (already counted in the total). */
export type OrderDiscounts = {
  discount_subtotal?: number; promotions?: { name: string; amount: number }[];
  reward?: { id: number; title: string; amount: number; gifts: string[] } | null;
};
/** What api/public/businesses/<slug>/pedido/<token>/ publishes (Receipt-Web-Server api/orders.py). */
type Tracked = {
  business: { name: string; slug: string; brand?: PublicBrand; whatsapp: string };
  order: {
    folio: string; status: 'open' | 'accepted' | 'ready' | 'paid' | 'canceled' | 'pending_payment'; status_label: string; fulfillment: string;
    fulfillment_label: string; created_at: string; items: OrderLine[]; subtotal: number; fee: number; amount: number; notes: string;
    customer_name: string; service_fee?: number; service_fee_label?: string; pending_prices?: number;
    // Paying online (the business's provider): paid, still waiting for the payment, and whether it can be paid now or later.
    paid_online?: boolean; payment_pending?: boolean; can_pay_online?: boolean; can_pay_later?: boolean;
    last_payment?: { status: string; detail: string | null } | null;
    /** What their saldo paid when they ordered, and what's still to pay (servers before them: absent). */
    balance_used?: number; due?: number;
  } & OrderDiscounts;
};

const STEPS = [
  { status: 'open', label: 'Recibido' },
  { status: 'accepted', label: 'En preparación' },
  { status: 'ready', label: 'Listo' },
  { status: 'paid', label: 'Entregado' },
];

/** receipt.com.mx/<slug>/pedido/<token>: an order's status for its customer, updated while it's on its way. */
const OrderPage = () => {
  const { slug = '', token = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const placedState = location.state as { placed?: boolean; notice?: string } | null;
  // Back from the payment page (Mercado Pago adds ?status=approved|rejected|… to this page's address).
  const returned = React.useMemo(() => new URLSearchParams(location.search).get('status') ?? new URLSearchParams(location.search).get('collection_status'), [location.search]);
  const placed = !!placedState?.placed || !!returned;
  const [data, setData] = React.useState<Tracked | null>(null);
  const [state, setState] = React.useState<'loading' | 'found' | 'missing'>('loading');
  const [paying, setPaying] = React.useState(false);
  const [payError, setPayError] = React.useState('');
  const api = `${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/pedido/${encodeURIComponent(token)}`;

  // The provider's parameters out of the address (the page keeps working if it's reloaded or shared).
  React.useEffect(() => {
    if (returned) navigate(location.pathname, { replace: true, state: { placed: true } });
  }, [returned, navigate, location.pathname]);

  const payNow = async () => {
    setPaying(true);
    setPayError('');
    try {
      const response = await fetch(`${api}/pay/`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      const body = await response.json().catch(() => ({}));
      if (response.ok && body.url) { window.location.href = body.url; return; }
      setPayError(body.detail || 'No se pudo abrir el pago. Intenta de nuevo.');
    } catch {
      setPayError('No hay conexión. Vuelve a intentar.');
    }
    setPaying(false);
  };
  const payLater = async () => {
    setPaying(true);
    setPayError('');
    try {
      const response = await fetch(`${api}/pay/`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ later: true }) });
      const body = await response.json().catch(() => ({}));
      if (response.ok && body.order) setData(current => (current ? { ...current, order: body.order } : current));
      else setPayError(body.detail || 'No se pudo cambiar. Intenta de nuevo.');
    } catch {
      setPayError('No hay conexión. Vuelve a intentar.');
    }
    setPaying(false);
  };

  React.useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    const load = () => fetch(`${process.env.REACT_APP_BASE_URL}/api/public/businesses/${encodeURIComponent(slug)}/pedido/${encodeURIComponent(token)}/`)
      .then(async response => {
        if (!alive) return;
        if (!response.ok) { setState('missing'); return; }
        const tracked: Tracked = await response.json();
        setData(tracked);
        setState('found');
        // While it's on its way, it updates by itself (sooner while its payment is being confirmed).
        if (['open', 'accepted', 'ready'].includes(tracked.order.status)) timer = window.setTimeout(load, 15000);
        else if (tracked.order.status === 'pending_payment') timer = window.setTimeout(load, 5000);
      })
      .catch(() => { if (alive) timer = window.setTimeout(load, 15000); });
    load();
    return () => { alive = false; window.clearTimeout(timer); };
  }, [slug, token]);

  const brand = data?.business.brand ?? DEFAULT_BRAND;
  useFontsStylesheet(brand.fonts_css);
  React.useEffect(() => {
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex';
    document.head.appendChild(robots);
    return () => { robots.remove(); };
  }, []);
  React.useEffect(() => { document.title = data ? `Pedido ${data.order.folio} · ${data.business.name}` : 'TRUE POS'; }, [data]);

  if (state !== 'found' || !data) {
    return (
      <>
        <CssBaseline />
        <Box sx={{ minHeight: '100vh', bgcolor: '#F2F5F7', display: 'grid', placeItems: 'center', px: 2, fontFamily: 'Inter, sans-serif' }}>
          <Box sx={{ width: '100%', maxWidth: 440, bgcolor: '#fff', borderRadius: '20px', p: 5, textAlign: 'center' }}>
            {state === 'loading' ? <CircularProgress size={32} sx={{ color: TRUE_DARK }} /> : (
              <>
                <Typography component="h1" sx={{ fontWeight: 700, fontSize: 24, color: TRUE_DARK }}>No encontramos este pedido</Typography>
                <Typography sx={{ color: TRUE_MUTED, mt: 1.5 }}>Revisa el enlace.</Typography>
              </>
            )}
          </Box>
        </Box>
      </>
    );
  }

  const { business, order } = data;
  const c = brand.colors;
  const heading = { fontFamily: fontStack(brand.fonts.heading), fontStretch: brand.fonts.heading.stretch ?? 'normal' };
  const reached = STEPS.findIndex(s => s.status === order.status);
  const whatsapp = business.whatsapp
    ? `https://wa.me/${business.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola, sobre mi pedido ${order.folio}`)}`
    : null;
  return (
    <>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: c.background, color: c.text, fontFamily: fontStack(brand.fonts.body), fontStretch: brand.fonts.body.stretch ?? 'normal' }}>
        <Box component="header" sx={{ bgcolor: c.surface, borderBottom: `1px solid ${c.border}` }}>
          <Box sx={{ maxWidth: 560, mx: 'auto', px: 2, py: 2.5, display: 'flex', alignItems: 'center', gap: 2 }}>
            {brand.logo
              ? <Box component="img" src={brand.logo} alt={business.name} sx={{ height: 52, maxWidth: 180, objectFit: 'contain' }} />
              : <Typography sx={{ fontWeight: 800, fontSize: 18, ...heading }}>{business.name}</Typography>}
          </Box>
        </Box>
        <Box component="main" sx={{ maxWidth: 560, mx: 'auto', px: 2, py: 3 }}>
          {placed && !order.payment_pending && (
            <Box sx={{ mb: 2, p: 2, borderRadius: '16px', bgcolor: c.accent, color: c.on_accent }}>
              <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>
                ¡Listo, {order.customer_name.split(' ')[0]}! {order.paid_online || (order.balance_used && !order.due) ? 'Recibimos tu pedido y tu pago.' : 'Recibimos tu pedido.'}
              </Typography>
              <Typography sx={{ fontSize: 14, fontFamily: 'inherit' }}>Guarda esta página: aquí ves cómo va.</Typography>
            </Box>
          )}
          {placedState?.notice && (
            <Box sx={{ mb: 2, p: 1.5, borderRadius: '12px', border: `1px solid ${c.border}`, bgcolor: c.surface }}>
              <Typography sx={{ fontSize: 14, fontFamily: 'inherit' }}>{placedState.notice}</Typography>
            </Box>
          )}
          {order.payment_pending && (
            <Box data-testid="payment-pending" sx={{ mb: 2, p: 2, borderRadius: '16px', bgcolor: c.surface, border: `1.5px solid ${c.primary}` }}>
              <Typography sx={{ fontWeight: 700, fontFamily: 'inherit' }}>
                {order.last_payment?.status === 'rejected' ? 'Tu pago no se aprobó' : 'Falta tu pago'}
              </Typography>
              <Typography sx={{ fontSize: 14, fontFamily: 'inherit', mb: 1.5 }}>
                {order.last_payment?.status === 'rejected'
                  ? 'Intenta de nuevo, con otra tarjeta si hace falta. Tu pedido llega al negocio en cuanto se apruebe.'
                  : `Tu pedido llega a ${business.name} en cuanto se apruebe tu pago.`}
              </Typography>
              {order.can_pay_online && (
                <Button onClick={payNow} disabled={paying} variant="contained" fullWidth data-testid="pay-now"
                  sx={{ py: 1.25, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', bgcolor: c.primary, color: c.on_primary,
                    boxShadow: 'none', '&:hover': { bgcolor: c.primary, boxShadow: 'none' } }}>
                  {paying ? 'Abriendo…' : `Pagar ${currency(order.due ?? order.amount)}`}
                </Button>
              )}
              {order.can_pay_later && (
                <Button onClick={payLater} disabled={paying} fullWidth data-testid="pay-later"
                  sx={{ mt: 1, textTransform: 'none', fontWeight: 600, fontFamily: 'inherit', color: c.text }}>
                  Prefiero pagar {order.fulfillment === 'delivery' ? 'al recibirlo' : 'al recogerlo'}
                </Button>
              )}
              {payError && <Typography role="alert" sx={{ color: '#C0392B', fontSize: 14, mt: 1, fontFamily: 'inherit' }}>{payError}</Typography>}
            </Box>
          )}
          <Typography sx={{ color: c.muted, fontSize: 14, fontFamily: 'inherit' }}>Pedido {order.folio} · {dayjs(order.created_at).format('D MMM, HH:mm')}</Typography>
          <Typography component="h1" data-testid="order-status" sx={{ fontWeight: 800, fontSize: 28, ...heading }}>
            {order.status === 'canceled' ? 'Pedido cancelado' : order.payment_pending ? 'Esperando tu pago' : order.status_label}
          </Typography>

          {order.status !== 'canceled' && !order.payment_pending && (
            <Box sx={{ display: 'flex', gap: 0.75, mt: 2 }}>
              {STEPS.map((step, i) => (
                <Box key={step.status} sx={{ flex: 1 }}>
                  <Box sx={{ height: 6, borderRadius: 3, bgcolor: i <= reached ? c.primary : c.border }} />
                  <Typography sx={{ fontSize: 12, mt: 0.5, color: i <= reached ? c.text : c.muted, fontWeight: i === reached ? 700 : 400, fontFamily: 'inherit' }}>
                    {i < reached && <CheckIcon sx={{ fontSize: 12, verticalAlign: '-2px', mr: 0.25 }} />}{step.label}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
          <Typography sx={{ mt: 2, fontFamily: 'inherit' }} data-testid="order-payment">
            {order.fulfillment === 'delivery' ? 'Te lo llevamos a domicilio' : 'Lo recoges en tienda'};{' '}
            {order.paid_online ? <strong>pagado en línea ✓</strong>
              : order.balance_used && !order.due ? <strong>pagado con tu saldo ✓</strong>
              : order.payment_pending ? `pagas ${order.balance_used ? 'el resto ' : ''}en línea.`
              : `pagas ${order.balance_used ? `${currency(order.due ?? 0)} ` : ''}${order.fulfillment === 'delivery' ? 'al recibirlo' : 'al recogerlo'}.`}
          </Typography>
          {!order.payment_pending && order.can_pay_online && (
            <Button onClick={payNow} disabled={paying} size="small" data-testid="pay-ahead"
              sx={{ mt: 0.5, px: 0, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', color: c.text, textDecoration: 'underline' }}>
              {paying ? 'Abriendo…' : `Pagar ahora en línea (${currency(order.due ?? order.amount)})`}
            </Button>
          )}
          {!order.payment_pending && payError && <Typography role="alert" sx={{ color: '#C0392B', fontSize: 14, fontFamily: 'inherit' }}>{payError}</Typography>}

          <Box sx={{ mt: 2.5, bgcolor: c.surface, border: `1px solid ${c.border}`, borderRadius: '16px', overflow: 'hidden' }}>
            {order.items.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, p: 1.5, borderTop: i ? `1px solid ${c.border}` : 'none' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 600, fontFamily: 'inherit' }}>{item.quantity} × {item.name}</Typography>
                  {item.options.length > 0 && <Typography sx={{ color: c.muted, fontSize: 13, fontFamily: 'inherit' }}>{item.options.map(o => o.name).join(', ')}</Typography>}
                </Box>
                <Typography sx={{ fontFamily: 'inherit', flexShrink: 0, color: item.confirmed === false ? c.muted : 'inherit' }}>
                  {item.confirmed === false && !item.price ? 'Por cotizar' : `${currency(item.price * item.quantity)}${item.confirmed === false ? ' aprox.' : ''}`}
                </Typography>
              </Box>
            ))}
            <Box sx={{ p: 1.5, borderTop: `1px solid ${c.border}` }}>
              {(order.promotions ?? []).map((promo, i) => (
                <Box key={i} data-testid="order-promotion" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}><span>{promo.name}</span><span>−{currency(promo.amount)}</span></Box>
              ))}
              {order.reward && (
                <Box data-testid="order-reward" sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <span>Recompensa: {order.reward.title}{order.reward.gifts.length ? ` (te damos: ${order.reward.gifts.join(', ')})` : ''}</span>
                  <span>{order.reward.amount ? `−${currency(order.reward.amount)}` : ''}</span>
                </Box>
              )}
              {order.fee > 0 && <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>Envío</span><span>{currency(order.fee)}</span></Box>}
              {!!order.service_fee && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><span>{order.service_fee_label || 'Comisión por servicio'}</span><span>{currency(order.service_fee)}</span></Box>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 18 }}>
                <span>{order.pending_prices ? 'Total aproximado' : 'Total'}</span>
                <span data-testid="order-total">{order.items.some(i => i.confirmed === false && !i.price) ? 'Por confirmar' : currency(order.amount)}</span>
              </Box>
              {!!order.balance_used && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }} data-testid="order-balance">
                    <span>Pagado con tu saldo</span><span>−{currency(order.balance_used)}</span>
                  </Box>
                  {!!order.due && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}><span>Por pagar</span><span>{currency(order.due)}</span></Box>
                  )}
                </>
              )}
              {!!order.pending_prices && (
                <Typography sx={{ color: c.muted, fontSize: 13, mt: 0.5, fontFamily: 'inherit' }}>
                  Te confirmamos el total al comprar tus productos; esta página se actualiza sola.
                </Typography>
              )}
            </Box>
          </Box>

          {whatsapp && (
            <Button href={whatsapp} target="_blank" rel="noopener" variant="outlined" startIcon={<WhatsAppIcon />} fullWidth
              sx={{ mt: 2.5, py: 1.25, borderRadius: 99, textTransform: 'none', fontWeight: 700, fontFamily: 'inherit', color: c.text, borderColor: c.border }}>
              Escribir a {business.name}
            </Button>
          )}
          <Button component={Link} to={`/${business.slug}`} fullWidth
            sx={{ mt: 1, textTransform: 'none', fontWeight: 600, fontFamily: 'inherit', color: c.muted }}>
            Volver al {brand.catalog_label?.toLowerCase() || 'menú'}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default OrderPage;
